import { forwardRef } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { Loader2 } from 'lucide-react'
import { cn } from '../cn'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link'
type Size = 'sm' | 'md' | 'lg' | 'icon'

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-accent-solid text-accent-solid-ink border border-transparent hover:bg-accent-solid-hover active:translate-y-px',
  secondary:
    'bg-surface text-ink border border-rule-strong hover:bg-sunken active:translate-y-px',
  ghost:
    'bg-transparent text-ink-muted border border-transparent hover:bg-sunken hover:text-ink',
  danger:
    'bg-danger text-danger-ink border border-transparent hover:brightness-110 active:translate-y-px',
  link: 'bg-transparent text-accent border border-transparent underline-offset-4 hover:underline p-0 h-auto',
}

const SIZES: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-xs gap-1.5',
  md: 'h-8 px-3 text-base gap-2',
  lg: 'h-10 px-4 text-md gap-2',
  icon: 'h-8 w-8 p-0 justify-center',
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  asChild?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'secondary', size = 'md', loading = false, asChild = false, children, disabled, ...props },
  ref,
) {
  const classes = cn(
    'inline-flex items-center rounded-[var(--radius-control)] font-medium whitespace-nowrap',
    'transition-colors duration-[var(--duration-fast)] ease-(--ease-out)',
    'disabled:pointer-events-none disabled:opacity-50',
    VARIANTS[variant],
    variant !== 'link' && SIZES[size],
    className,
  )

  // Radix's Slot merges onto exactly one child. Rendering the spinner slot
  // alongside `children` would hand it two — which throws and takes the whole
  // tree down — so an asChild button styles its child and nothing more. A
  // link cannot show a pending state anyway.
  if (asChild) {
    return (
      <Slot ref={ref} className={classes} {...props}>
        {children}
      </Slot>
    )
  }

  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      data-loading={loading || undefined}
      className={classes}
      {...props}
    >
      {loading ? <Loader2 aria-hidden className="size-4 animate-spin" /> : null}
      {children}
    </button>
  )
})
